/**
 * GuidedCreationPlugin - Step-by-step guided activity type.
 *
 * New plugin for the Activity Engine. Guides children through
 * a sequence of steps (drag specific blocks), then opens free play.
 * Designed for pre-readers with icon-only prompts.
 *
 * @see GitHub Issue #852
 */

import ScratchJr from '../../../ScratchJr';
import ActivityStorageService from '../../storage/ActivityStorageService';
import StepIndicator from './StepIndicator';
import PaletteRestrictor from './PaletteRestrictor';
import {scorePreferredNarrationVoice} from '../../shared/SpeechVoicePreference';

export default class GuidedCreationPlugin {
    constructor () {
        this._activityData = null;
        this._stateMachine = null;
        this._container = null;
        this._stepIndicator = null;
        this._paletteRestrictor = null;
        this._steps = [];
        this._currentStepIndex = 0;
        this._completedSteps = [];
        this._freePlayBlocks = 0;
        this._bonusesEarned = [];
        this._blockCheckInterval = null;
        this._pageVoPlayed = {};
        this._lastPageIndex = -1;
        this._pageMonitor = null;
        this._lastNarrationId = null;
        this._lastNarrationText = null;
        this._speechVoice = null;
        this._doneBtn = null;
        this._eligibilityCheckInterval = null;
    }

    /**
     * Initialize the plugin for a specific activity.
     * @param {object} activityData - Validated activity definition
     * @param {object} stateMachine - ActivityStateMachine instance
     * @param {HTMLElement} container - Editor container
     */
    init (activityData, stateMachine, container) {
        this._activityData = activityData;
        this._stateMachine = stateMachine;
        this._container = container;
        this._steps = activityData.scaffolding.steps || [];
        this._engagementMode = this._steps.length === 0;

        if (this._engagementMode && activityData.voiceOver) {
            const voKeys = Object.keys(activityData.voiceOver);
            for (const key of voKeys) {
                const match = key.match(/^VO-PAGE(\d+)$/);
                if (match) {
                    this._pageVoPlayed[parseInt(match[1], 10)] = false;
                }
            }
            this._startPageMonitor();
        }

        if (this._engagementMode) {
            this._startEligibilityCheck();
        }

        this._projectHasRun = false;
        this._loadSavedProgress(activityData.activityId);
        this._completedSteps = [];
        this._freePlayBlocks = 0;
        this._bonusesEarned = [];

        // Set up palette restrictor
        if (activityData.scaffolding.availableBlocks) {
            this._paletteRestrictor = new PaletteRestrictor(
                container,
                activityData.scaffolding.availableBlocks
            );
        }

        // Set up step indicator
        this._stepIndicator = new StepIndicator(container, this._steps);

        // Start block monitoring for step completion (step-based only)
        if (!this._engagementMode) {
            this._startBlockMonitoring();
        }
    }

    /**
     * React to state machine transitions.
     */
    onStateChange (prevState, newState, meta) {
        switch (newState) {
        case 'GUIDED_STEP':
            this._currentStepIndex = meta.stepIndex || 0;
            this._showCurrentStep();
            break;

        case 'FREE_PLAY':
            this._enterFreePlay();
            break;

        case 'EVALUATING':
            this._evaluateBonuses();
            break;

        case 'READY':
            this._resetUI();
            break;

        case 'SUCCESS':
            this._persistProgress({status: 'completed', completedAt: new Date().toISOString()});
            break;
        }
    }

    /**
     * Record that the project has been run (engagement mode).
     */
    markProjectRun () {
        this._projectHasRun = true;
        this._persistProgress();
    }

    /**
     * Evaluate current state against success criteria.
     * @returns {object}
     */
    evaluate () {
        if (this._engagementMode) {
            const pagesWithScripts = this._countPagesWithScripts();
            const minPagesRequired = this._activityData.successCriteria.minPagesWithScripts || 1;
            const projectRun = this._projectHasRun;
            return {
                pagesWithScripts,
                minPagesRequired,
                projectRun,
                success: pagesWithScripts >= minPagesRequired && projectRun
            };
        }
        return {
            stepsCompleted: this._completedSteps.length,
            totalSteps: this._steps.length,
            bonusesEarned: this._bonusesEarned,
            freePlayBlocks: this._freePlayBlocks,
            success: this._completedSteps.length >= (this._activityData.successCriteria.requiredSteps || this._steps.length)
        };
    }

    /**
     * Get current block count in scripting area.
     * @returns {number}
     */
    getBlockCount () {
        return this._countBlocks();
    }

    /**
     * Show a hint (from HintManager).
     * @param {object} hint
     */
    showHint (hint) {
        if (hint.tier === 0 && this._stepIndicator) {
            // Tier 0: text nudge — pulse the current step indicator
            this._stepIndicator.pulseCurrentStep();
        } else if (hint.tier === 1 && hint.svg) {
            // Tier 1: SVG overlay showing the target action
            this._showSVGOverlay(hint.svg);
        } else if (hint.tier === 2) {
            // Tier 2: Explicit block-by-block guidance
            this._stepIndicator.showExplicitGuide(this._currentStepIndex);
        }
    }

    /**
     * Show full walkthrough (give-up scenario).
     */
    showWalkthrough () {
        if (this._stepIndicator) {
            this._stepIndicator.showAllSteps();
        }
    }

    destroy () {
        this._stopPageMonitor();
        this._stopNarration();
        this._hideDoneButton();
        if (this._eligibilityCheckInterval) {
            clearInterval(this._eligibilityCheckInterval);
            this._eligibilityCheckInterval = null;
        }
        if (this._blockCheckInterval) {
            clearInterval(this._blockCheckInterval);
        }
        if (this._stepIndicator) {
            this._stepIndicator.destroy();
        }
        if (this._paletteRestrictor) {
            this._paletteRestrictor.destroy();
        }
        this._activityData = null;
        this._stateMachine = null;
    }

    // --- Private methods ---

    _showCurrentStep () {
        if (!this._stepIndicator) return;
        const step = this._steps[this._currentStepIndex];
        if (!step) return;

        this._stepIndicator.showStep(this._currentStepIndex);

        // Restrict palette to blocks available for this step (if specified)
        if (this._paletteRestrictor) {
            this._paletteRestrictor.apply();
        }
    }

    _enterFreePlay () {
        // Remove palette restrictions — all blocks available
        if (this._paletteRestrictor) {
            this._paletteRestrictor.remove();
        }

        // Hide step indicator, show free play prompt
        if (this._stepIndicator) {
            this._stepIndicator.showFreePlayPrompt();
        }

        // Track blocks added during free play
        this._freePlayBlocks = 0;
        const baseCount = this._countBlocks();
        if (this._blockCheckInterval) clearInterval(this._blockCheckInterval);
        this._blockCheckInterval = setInterval(() => {
            const current = this._countBlocks();
            this._freePlayBlocks = Math.max(0, current - baseCount);
        }, 500);
    }

    _evaluateBonuses () {
        const criteria = this._activityData.successCriteria;
        if (!criteria.bonusCriteria) return;

        for (const bonus of criteria.bonusCriteria) {
            if (this._checkBonusCriteria(bonus)) {
                this._bonusesEarned.push(bonus.name);
            }
        }
    }

    _checkBonusCriteria (bonus) {
        const check = bonus.check;
        if (!check) return false;

        if (check.startsWith('blockCount')) {
            // e.g., "blockCount > 6"
            const match = check.match(/blockCount\s*([><=]+)\s*(\d+)/);
            if (match) {
                const op = match[1];
                const val = parseInt(match[2], 10);
                const count = this._countBlocks();
                switch (op) {
                case '>': return count > val;
                case '>=': return count >= val;
                case '<': return count < val;
                case '==': return count === val;
                }
            }
        }

        if (check.startsWith('hasCategory:')) {
            const category = check.replace('hasCategory:', '');
            return this._hasBlockCategory(category);
        }

        return false;
    }

    _startBlockMonitoring () {
        // Poll for block additions to detect step completion
        this._blockCheckInterval = setInterval(() => {
            if (this._stateMachine.state !== 'GUIDED_STEP') return;
            const step = this._steps[this._currentStepIndex];
            if (!step) return;

            if (this._isStepComplete(step)) {
                this._completeCurrentStep();
            }
        }, 300);
    }

    _isStepComplete (step) {
        if (!step.successCheck) return false;

        // Parse successCheck string
        if (step.successCheck.startsWith('hasBlock:')) {
            const blockType = step.successCheck.replace('hasBlock:', '');
            return this._hasBlock(blockType);
        }

        return false;
    }

    _completeCurrentStep () {
        if (this._completedSteps.includes(this._currentStepIndex)) return;
        this._completedSteps.push(this._currentStepIndex);

        // Play step celebration
        const step = this._steps[this._currentStepIndex];
        if (this._stepIndicator) {
            this._stepIndicator.celebrateStep(this._currentStepIndex, step.celebration || 'sparkle');
        }

        // Advance to next step or free play
        const nextIndex = this._currentStepIndex + 1;
        if (nextIndex < this._steps.length) {
            // Small delay for celebration before advancing
            setTimeout(() => {
                this._stateMachine.transition('GUIDED_STEP', {stepIndex: nextIndex});
            }, 800);
        } else {
            // All steps complete — enter free play
            setTimeout(() => {
                this._stateMachine.transition('FREE_PLAY');
            }, 1200);
        }
    }

    _resetUI () {
        this._completedSteps = [];
        this._freePlayBlocks = 0;
        this._bonusesEarned = [];
        this._currentStepIndex = 0;
        this._hideDoneButton();
        if (this._stepIndicator) {
            this._stepIndicator.reset();
        }
    }

    _loadSavedProgress (activityId) {
        ActivityStorageService.getActivityProgress(activityId).then((saved) => {
            if (saved && this._activityData) {
                this._hydrateFromProgress(saved);
            }
        });
    }

    _hydrateFromProgress (saved) {
        if (saved.pageVoPlayed) {
            for (const key of Object.keys(saved.pageVoPlayed)) {
                const pageNum = parseInt(key, 10);
                if (pageNum in this._pageVoPlayed) {
                    this._pageVoPlayed[pageNum] = saved.pageVoPlayed[key];
                }
            }
        }
        if (saved.projectHasRun) {
            this._projectHasRun = true;
        }
        if (typeof saved.lastPageIndex === 'number') {
            this._lastPageIndex = saved.lastPageIndex;
        }
    }

    _persistProgress (overrides = {}) {
        if (!this._activityData) return;
        ActivityStorageService.saveProgress(this._activityData.activityId, {
            status: overrides.status || 'in_progress',
            pageVoPlayed: {...this._pageVoPlayed},
            projectHasRun: this._projectHasRun,
            lastPageIndex: this._lastPageIndex,
            completedAt: overrides.completedAt
        });
    }

    _startEligibilityCheck () {
        if (this._eligibilityCheckInterval) {
            clearInterval(this._eligibilityCheckInterval);
        }
        this._eligibilityCheckInterval = setInterval(() => {
            this._checkDoneEligibility();
        }, 1000);
    }

    _checkDoneEligibility () {
        if (!this._activityData) return;
        const pagesWithScripts = this._countPagesWithScripts();
        const minRequired = this._activityData.successCriteria.minPagesWithScripts || 1;
        if (pagesWithScripts >= minRequired && this._projectHasRun) {
            this._showDoneButton();
        } else {
            this._hideDoneButton();
        }
    }

    _showDoneButton () {
        if (this._doneBtn) {
            return;
        }
        this._doneBtn = document.createElement('button');
        this._doneBtn.className = 'activity-guided-done-btn';
        this._doneBtn.type = 'button';
        this._doneBtn.setAttribute('aria-label', 'Done');
        this._doneBtn.textContent = 'Done';
        this._doneBtn.addEventListener('pointerup', () => {
            this._stateMachine.transition('EVALUATING');
        });
        this._container.appendChild(this._doneBtn);
    }

    _hideDoneButton () {
        if (this._doneBtn && this._doneBtn.parentNode) {
            this._doneBtn.parentNode.removeChild(this._doneBtn);
        }
        this._doneBtn = null;
    }

    _startPageMonitor () {
        this._stopPageMonitor();
        this._pageMonitor = setInterval(() => {
            const index = ScratchJr.stage
                ? ScratchJr.stage.pages.indexOf(ScratchJr.stage.currentPage)
                : -1;
            if (index !== this._lastPageIndex) {
                this._lastPageIndex = index;
                this._onPageChanged(index);
            }
        }, 400);
    }

    _stopPageMonitor () {
        if (this._pageMonitor) {
            clearInterval(this._pageMonitor);
            this._pageMonitor = null;
        }
    }

    _onPageChanged (pageIndex) {
        const pageNum = pageIndex + 1;
        if (this._pageVoPlayed[pageNum] === false) {
            this._pageVoPlayed[pageNum] = true;
            this._speak('VO-PAGE' + pageNum);
            this._persistProgress();
        }
    }

    _speak (voiceId, fallbackText, opts = {}) {
        const text = fallbackText || (this._activityData.voiceOver && this._activityData.voiceOver[voiceId]);
        if (!text) {
            if (opts.onend) {
                opts.onend();
            }
            return;
        }

        this._lastNarrationId = voiceId;
        this._lastNarrationText = text;

        if (!window.speechSynthesis || typeof SpeechSynthesisUtterance === 'undefined') {
            if (opts.onend) {
                opts.onend();
            }
            return;
        }

        try {
            window.speechSynthesis.cancel();
            const utterance = new SpeechSynthesisUtterance(text);
            const preferredVoice = this._getPreferredVoice();
            utterance.lang = preferredVoice && preferredVoice.lang ? preferredVoice.lang : 'en-US';
            if (preferredVoice) {
                utterance.voice = preferredVoice;
            }
            utterance.rate = 0.85;
            utterance.pitch = 1.05;
            utterance.volume = 1;
            utterance.onend = () => {
                if (opts.onend) {
                    opts.onend();
                }
            };
            window.speechSynthesis.speak(utterance);
        } catch (_) {
            if (opts.onend) {
                opts.onend();
            }
        }
    }

    _stopNarration () {
        if (window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
            try {
                window.speechSynthesis.cancel();
            } catch (_) {}
        }
    }

    _getPreferredVoice () {
        if (!this._speechVoice) {
            this._refreshPreferredVoice();
        }
        return this._speechVoice;
    }

    _refreshPreferredVoice () {
        const selection = this._selectPreferredVoice();
        this._speechVoice = selection ? selection.voice : null;
    }

    _selectPreferredVoice () {
        if (!window.speechSynthesis || typeof window.speechSynthesis.getVoices !== 'function') {
            return null;
        }
        const voices = window.speechSynthesis.getVoices();
        if (!voices || voices.length === 0) {
            return null;
        }
        let best = null;
        let bestScore = -Infinity;
        for (const voice of voices) {
            const score = scorePreferredNarrationVoice(voice, window);
            if (score > bestScore) {
                bestScore = score;
                best = voice;
            }
        }
        return best ? {voice: best, score: bestScore} : null;
    }

    // --- ScratchJr integration helpers ---

    /**
     * Count pages that contain at least one script block group.
     * @returns {number}
     */
    _countPagesWithScripts () {
        const pages = this._container.querySelectorAll('.page-container, .proj-page');
        let count = 0;
        for (const page of pages) {
            if (page.querySelector('.scripts .blockgroup')) {
                count++;
            }
        }
        return count;
    }

    /**
     * Count blocks in the current project's scripting area.
     * Uses ScratchJr's internal DOM structure.
     */
    _countBlocks () {
        // ScratchJr renders blocks as SVG elements in the scripts container
        const scripts = this._container.querySelectorAll('.scripts .blockgroup');
        return scripts ? scripts.length : 0;
    }

    /**
     * Check if a specific block type exists in the scripting area.
     * @param {string} blockType - e.g., 'forward', 'hop', 'repeat'
     */
    _hasBlock (blockType) {
        // ScratchJr blocks have data attributes or class names indicating type
        const blocks = this._container.querySelectorAll('.scripts [data-block-type]');
        for (const block of blocks) {
            if (block.getAttribute('data-block-type') === blockType) {
                return true;
            }
        }
        // Fallback: check by block class naming convention
        const byClass = this._container.querySelectorAll(`.scripts .block-${blockType}`);
        return byClass && byClass.length > 0;
    }

    /**
     * Check if any block from a category exists.
     * @param {string} category - e.g., 'looks', 'sound'
     */
    _hasBlockCategory (category) {
        const blocks = this._container.querySelectorAll('.scripts [data-block-category]');
        for (const block of blocks) {
            if (block.getAttribute('data-block-category') === category) {
                return true;
            }
        }
        return false;
    }

    _showSVGOverlay (svgPath) {
        const overlay = document.createElement('div');
        overlay.className = 'activity-hint-svg-overlay';
        const img = document.createElement('img');
        img.src = svgPath;
        img.className = 'activity-hint-svg';
        overlay.appendChild(img);
        overlay.addEventListener('pointerup', () => {
            if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        });
        this._container.appendChild(overlay);
    }
}
